/**
 * AuthContext - SIMPLIFIED Frontend Layer
 * 
 * This context is a PURE PRESENTATION LAYER that:
 * - Only manages UI state (loading, error, user info)
 * - Makes API calls to backend
 * - Does NOT handle authentication logic
 * - All security logic resides on the backend
 * 
 * Backend acts as the SINGLE SOURCE OF TRUTH for:
 * - Session management
 * - Token generation and validation
 * - User authentication
 * - OAuth flows
 * - Password reset flows
 * - Email verification
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { authApi } from '../services/authApi';
import { scannerApi } from '../services/scannerApi';
import { setAuthToken, setRefreshToken, removeAuthToken } from '../config/api';

const USERNAME_SANITIZE_PATTERN = /[^a-zA-Z0-9_-]/g;
const MAX_USERNAME_LENGTH = 50;

const deriveUsernameFromEmail = (email) => {
  const localPart = (email || '').trim().toLowerCase().split('@')[0] || 'user';
  return localPart.replace(USERNAME_SANITIZE_PATTERN, '_').slice(0, MAX_USERNAME_LENGTH) || 'user';
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [scans, setScans] = useState([]);

  const fetchUserScans = useCallback(async () => {
    try {
      const scanData = await scannerApi.getUserScans();
      setScans(scanData || []);
    } catch (error) {
      console.error('[AuthContext] Failed to fetch scans:', error);
    }
  }, []);

  /**
   * Initialize authentication on app load
   * Backend validates the stored token by checking if it's still valid
   */
  const initializeAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');

      if (!token) {
        // Fallback: Check if there's an access_token in the URL hash (happens if redirected to root)
        const hash = window.location.hash || '';
        if (hash.includes('access_token=')) {
          console.log('[AuthContext] Detected access_token in URL hash, redirecting to callback handler');
          const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
          const accessToken = hashParams.get('access_token');
          const refreshTokenValue = hashParams.get('refresh_token');

          if (accessToken) {
            try {
              const response = await authApi.handleOAuthCallback({
                access_token: accessToken,
                refresh_token: refreshTokenValue,
                provider: 'oauth-detect'
              });

              setAuthToken(response.access_token);
              if (response.refresh_token) {
                setRefreshToken(response.refresh_token);
              }
              setUser(response.user);
              setIsAuthenticated(true);
              window.history.replaceState(null, document.title, window.location.pathname);
              toast.success('Sign-in successful!');
              await fetchUserScans();
              setLoading(false);
              return;
            } catch (err) {
              console.error('[AuthContext] Hash-based OAuth exchange failed:', err);
              toast.error('Authentication failed. Please try again.');
            }
          }
        }
        setLoading(false);
        return;
      }

      try {
        // Ask backend to verify current user (validates token)
        const userData = await authApi.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);

        // Load user's scans
        try {
          await fetchUserScans();
        } catch (error) {
          console.warn('[AuthContext] Unable to load scans during init:', error);
        }
      } catch (error) {
        console.error('[AuthContext] Token validation failed:', error);
        // Token is invalid - clear it
        removeAuthToken();
        localStorage.removeItem('refresh_token');
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchUserScans]);

  // Initialize auth on component mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  /**
   * Login - Frontend ONLY submits credentials to backend
   * Backend handles all authentication logic and returns tokens
   */
  const login = async (email, password) => {
    try {
      setLoading(true);
      const sanitizedEmail = (email || '').trim().toLowerCase();

      console.log('[AuthContext] Calling backend login for:', sanitizedEmail);

      // Backend handles all auth logic, returns tokens and user data
      const response = await authApi.login(sanitizedEmail, password);

      // Store tokens returned by backend
      setAuthToken(response.access_token);
      if (response.refresh_token) {
        setRefreshToken(response.refresh_token);
      }

      // Update frontend UI state only
      setUser(response.user);
      setIsAuthenticated(true);

      // Load user's scans
      try {
        await fetchUserScans();
      } catch (error) {
        console.warn('[AuthContext] Unable to load scans after login:', error);
      }

      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Login API call failed:', error);
      const errorMessage = typeof error === 'string' ? error : error?.message || 'Invalid email or password.';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Register - Frontend ONLY submits user data to backend
   * Backend handles user creation, tokens, email verification
   */
  const register = async (userData) => {
    try {
      setLoading(true);
      const sanitizedEmail = (userData.email || '').trim().toLowerCase();
      const sanitizedName = (userData.name || userData.fullName || '').trim();
      const sanitizedUsername = (userData.username || deriveUsernameFromEmail(sanitizedEmail)).trim().toLowerCase();

      // Backend handles all registration logic, returns tokens and user data
      const response = await authApi.register({
        email: sanitizedEmail,
        username: sanitizedUsername,
        password: userData.password,
        confirm_password: userData.confirmPassword || userData.confirm_password || userData.password,
        full_name: sanitizedName || undefined,
      });

      // Store tokens returned by backend
      setAuthToken(response.access_token);
      if (response.refresh_token) {
        setRefreshToken(response.refresh_token);
      }

      // Update frontend UI state only
      setUser(response.user);
      setIsAuthenticated(true);

      toast.success('Registration successful! Check your email to verify your account.', { duration: 6000 });
      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Registration API call failed:', error);
      const errorMessage = typeof error === 'string' ? error : error?.message || 'Unable to create account.';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout - Frontend notifies backend to invalidate token
   * Backend revokes tokens, frontend clears local state
   */
  const logout = async ({ silent = false } = {}) => {
    try {
      // Notify backend to invalidate current token
      try {
        await authApi.logout();
      } catch (error) {
        console.warn('[AuthContext] Logout API call failed (continuing anyway):', error);
      }
    } finally {
      // Clear local state regardless
      removeAuthToken();
      localStorage.removeItem('refresh_token');
      setUser(null);
      setIsAuthenticated(false);
      setScans([]);

      if (!silent) {
        toast.success('Logged out successfully');
      }
    }
  };

  /**
   * Refresh token - Ask backend to generate new access token
   * Backend validates refresh token and returns new access token
   */
  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      // Backend validates refresh token and returns new access token
      const response = await authApi.refreshToken(refreshTokenValue);

      // Update access token
      setAuthToken(response.access_token);

      // Update refresh token if backend returns new one
      if (response.refresh_token) {
        setRefreshToken(response.refresh_token);
      }

      console.log('[AuthContext] Token refreshed by backend');
      return response.access_token;
    } catch (error) {
      console.error('[AuthContext] Token refresh failed:', error);
      // Refresh failed - logout user
      await logout({ silent: true });
      throw error;
    }
  };

  /**
   * Update profile - Send to backend, backend validates and updates
   */
  const updateProfile = async (profileData) => {
    try {
      const allowedPreferenceKeys = new Set([
        'timezone',
        'security_digest',
        'next_security_review',
        'scan_default_mode',
        'scan_default_depth',
        'primary_alert_channel',
        'alert_severity',
        'daily_digest_enabled',
      ]);
      const sanitizedPreferences =
        profileData.preferences && typeof profileData.preferences === 'object'
          ? Object.fromEntries(
            Object.entries(profileData.preferences).filter(([key]) => allowedPreferenceKeys.has(key))
          )
          : undefined;
      const payload = {
        full_name: profileData.full_name?.trim() || undefined,
        username: profileData.username?.trim() || undefined,
        preferences:
          sanitizedPreferences && Object.keys(sanitizedPreferences).length > 0
            ? sanitizedPreferences
            : undefined,
      };

      // Backend validates and updates profile
      const updatedUser = await authApi.updateProfile(payload);
      setUser(updatedUser);
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      const message = typeof error === 'string' ? error : error?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  /**
   * Change password - Backend validates current password and updates new one
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      // Backend validates current password and updates new password
      await authApi.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      const message = typeof error === 'string' ? error : error?.message || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  /**
   * Set password - Backend updates password for the authenticated account
   */
  const setPassword = async (newPassword) => {
    try {
      await authApi.setPassword(newPassword);
      toast.success('Password set successfully');
      return { success: true };
    } catch (error) {
      const message = typeof error === 'string' ? error : error?.message || 'Password set failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  /**
   * Request password reset - Backend sends email
   */
  const requestPasswordReset = async (email) => {
    try {
      // Backend handles email sending securely
      await authApi.forgotPassword(email);
      // Always return generic success to prevent email enumeration
      toast.success('If an account exists, a reset email has been sent.', { duration: 5000 });
      return { success: true };
    } catch (error) {
      console.error('[AuthContext] Password reset request failed:', error);
      // Show generic message for security
      toast.success('If an account exists, a reset email has been sent.', { duration: 5000 });
      return { success: true };
    }
  };

  /**
   * Reset password - Backend validates token and updates password
   */
  const resetPassword = async (token, newPassword) => {
    try {
      // Backend validates token and updates password
      await authApi.resetPassword(token, newPassword);
      toast.success('Password reset successful!');
      return { success: true };
    } catch (error) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to reset password';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  /**
   * Resend verification email - Backend sends new verification email
   */
  const resendEmailVerification = async (email) => {
    try {
      // Backend handles email sending securely  
      await authApi.resendVerificationEmail(email);
      toast.success('Verification email sent! Check your inbox.', { duration: 5000 });
      return { success: true };
    } catch (error) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to resend email';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  /**
   * Verify email - Backend validates token and marks user as verified
   */
  const verifyEmail = async (token) => {
    try {
      // Backend validates token and marks user as verified in database
      const response = await authApi.verifyEmail(token);
      toast.success('Email verified successfully!');
      return { success: true, message: response.message };
    } catch (error) {
      const message = typeof error === 'string' ? error : error?.message || 'Failed to verify email';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  /**
   * Get OAuth authorization URL - Backend returns URL to start OAuth flow
   * Frontend redirects to Supabase OAuth endpoint (handled by backend)
   */
  const getOAuthUrl = async (provider) => {
    try {
      const redirectTo = typeof window !== 'undefined'
        ? `${window.location.origin}/auth/callback`
        : undefined;
      // Backend returns OAuth authorization URL constructed by backend
      const { auth_url } = await authApi.getOAuthAuthorizationUrl(provider, redirectTo);
      return { success: true, url: auth_url };
    } catch (error) {
      const message = typeof error === 'string' ? error : error?.message || `Failed to initiate ${provider} login`;
      toast.error(message);
      return { success: false, error: message };
    }
  };

  /**
   * Handle OAuth callback - Backend exchanges OAuth tokens for JWT
   */
  const handleOAuthCallback = async (tokens) => {
    try {
      // Backend handles OAuth token validation, creates user if needed, returns JWT
      const response = await authApi.handleOAuthCallback(tokens);

      // Store JWT tokens from backend
      setAuthToken(response.access_token);
      if (response.refresh_token) {
        setRefreshToken(response.refresh_token);
      }

      // Update frontend UI state only
      setUser(response.user);
      setIsAuthenticated(true);

      toast.success('OAuth login successful!');
      return { success: true };
    } catch (error) {
      const message = typeof error === 'string' ? error : error?.message || 'OAuth authentication failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const addScan = (scan) => {
    setScans(prev => [scan, ...prev.slice(0, 49)]);
  };

  const updateScan = (scanId, updates) => {
    setScans(prev => prev.map(scan =>
      scan.scan_id === scanId ? { ...scan, ...updates } : scan
    ));
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    scans,
    // State setters (for OAuth callback and other special cases)
    setUser,
    setIsAuthenticated,
    // Core auth methods  
    login,
    register,
    logout,
    refreshToken,
    // Profile methods
    updateProfile,
    changePassword,
    setPassword,
    // Password reset methods
    requestPasswordReset,
    resetPassword,
    // Email verification methods
    resendEmailVerification,
    verifyEmail,
    // OAuth methods
    getOAuthUrl,
    handleOAuthCallback,
    // Scan management
    fetchUserScans,
    addScan,
    updateScan
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
